import { useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft } from 'lucide-react';
import { Badge, Button, Input, toast } from '@caratuva/ui';
import type { ConnectedAccountDTO, ConnectedAccountStatus } from '@caratuva/shared-types';
import {
  getConnectedAccount,
  resendConnectedAccountTos,
  saveConnectedAccountPayoutConfig,
} from '../lib/api';
import { CopyableField } from '../components/wizard';

/*
 * Customer (connected-account) detail — identity + KYC/KYB status, the bank
 * account / payout config, and the settlement-partner receiver id. Unifies the
 * former buyer + seller detail views. Integrator-admin read-only, except the
 * inline "add bank account" action for an approved customer that has no payout
 * yet (which flips it to invoice-able). The onboarding wizard lives at
 * /customers/new.
 */

export const Route = createFileRoute('/_app/customers/$id')({
  component: CustomerDetail,
});

const STATUS_VARIANT: Record<
  ConnectedAccountStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  created: 'outline',
  verifying: 'secondary',
  approved: 'default',
  rejected: 'destructive',
};

function CustomerDetail(): JSX.Element {
  const intl = useIntl();
  const { id } = Route.useParams();
  const query = useQuery({
    queryKey: ['connected-account', id],
    queryFn: ({ signal }) => getConnectedAccount(id, signal),
    // The receiver is created out-of-band: the BlindPay tos.accept webhook fires
    // the deferred submission server-side. Poll while the account is still
    // pre-terminal so the page reflects created -> verifying -> approved without
    // a manual refresh; stop once it settles (approved/rejected).
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'created' || status === 'verifying' ? 5000 : false;
    },
  });

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/customers"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'customers.title', defaultMessage: 'Customers' })}
      </Link>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'customers.detail.loading',
            defaultMessage: 'Loading customer...',
          })}
        </p>
      ) : query.isError ? (
        <p className="text-sm text-danger">
          {intl.formatMessage({
            id: 'customers.detail.error',
            defaultMessage: 'Failed to load customer.',
          })}
        </p>
      ) : (
        <CustomerDetailBody customer={query.data} />
      )}
    </section>
  );
}

function CustomerDetailBody({ customer }: { customer: ConnectedAccountDTO }): JSX.Element {
  const intl = useIntl();
  const displayName = customer.displayName ?? customer.legalName ?? customer.email ?? customer.id;

  return (
    <>
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900">{displayName}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={STATUS_VARIANT[customer.status]}>{customer.status}</Badge>
          {customer.readyToCollect ? (
            <Badge variant="default">
              {intl.formatMessage({ id: 'customers.invoiceable.yes', defaultMessage: 'Ready' })}
            </Badge>
          ) : (
            <Badge variant="outline">
              {intl.formatMessage({ id: 'customers.invoiceable.no', defaultMessage: 'Not yet' })}
            </Badge>
          )}
        </div>
      </header>

      <div className="rounded-md border border-navy-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({
            id: 'customers.detail.section.verification',
            defaultMessage: 'Verification',
          })}
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.entityType',
              defaultMessage: 'Entity type',
            })}
          >
            <span className="capitalize">{customer.entityType}</span>
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.status',
              defaultMessage: 'Verification status',
            })}
          >
            {customer.status}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.externalId',
              defaultMessage: 'External ID',
            })}
          >
            <span className="font-mono text-xs">{customer.externalId ?? '—'}</span>
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.email',
              defaultMessage: 'Email',
            })}
          >
            {customer.email ?? '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.country',
              defaultMessage: 'Country',
            })}
          >
            {customer.country ?? '—'}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.tos',
              defaultMessage: 'Terms accepted',
            })}
          >
            {customer.tosAccepted
              ? intl.formatMessage({ id: 'common.yes', defaultMessage: 'Yes' })
              : intl.formatMessage({ id: 'common.no', defaultMessage: 'No' })}
          </DetailField>
        </dl>

        {customer.status === 'created' && !customer.tosAccepted ? (
          <ResendTosLink customer={customer} />
        ) : null}

        {customer.tosAccepted && customer.submitPending ? <VerificationPending /> : null}
      </div>

      <div className="rounded-md border border-navy-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({
            id: 'customers.detail.section.bank',
            defaultMessage: 'Bank account',
          })}
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.payoutConfigured',
              defaultMessage: 'Bank account',
            })}
          >
            {customer.payoutConfigured ? (
              <Badge variant="default">
                {intl.formatMessage({
                  id: 'customers.detail.bank.set',
                  defaultMessage: 'Configured',
                })}
              </Badge>
            ) : customer.payoutPending ? (
              <Badge variant="secondary">
                {intl.formatMessage({
                  id: 'customers.detail.bank.pending',
                  defaultMessage: 'Pending terms acceptance',
                })}
              </Badge>
            ) : (
              <Badge variant="outline">
                {intl.formatMessage({
                  id: 'customers.detail.bank.unset',
                  defaultMessage: 'Not configured',
                })}
              </Badge>
            )}
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.readyToCollect',
              defaultMessage: 'Ready to collect',
            })}
          >
            {customer.readyToCollect
              ? intl.formatMessage({ id: 'common.yes', defaultMessage: 'Yes' })
              : intl.formatMessage({ id: 'common.no', defaultMessage: 'No' })}
          </DetailField>
        </dl>

        {customer.status === 'approved' && !customer.payoutConfigured ? (
          <AddBankAccount customer={customer} />
        ) : null}
      </div>

      <div className="rounded-md border border-navy-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({
            id: 'customers.detail.section.receiver',
            defaultMessage: 'Settlement receiver',
          })}
        </h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.receiverId',
              defaultMessage: 'Receiver ID',
            })}
          >
            <span className="font-mono text-xs break-all">{customer.receiverId ?? '—'}</span>
          </DetailField>
          <DetailField
            label={intl.formatMessage({
              id: 'customers.detail.field.connectedAccountId',
              defaultMessage: 'Connected account ID',
            })}
          >
            <span className="font-mono text-xs break-all">{customer.id}</span>
          </DetailField>
        </dl>
      </div>
    </>
  );
}

// Inline PIX registration for an approved customer with no bank account yet —
// the "add a bank account later" path that flips a verified customer to
// invoice-able (ready to collect).
function AddBankAccount({ customer }: { customer: ConnectedAccountDTO }): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();
  const [pixKey, setPixKey] = useState('');
  const [accountHolderName, setAccountHolderName] = useState(customer.legalName ?? '');

  const save = useMutation({
    mutationFn: () =>
      saveConnectedAccountPayoutConfig(customer.id, {
        pixKey: pixKey.trim(),
        accountHolderName: accountHolderName.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['connected-account', customer.id] });
      void qc.invalidateQueries({ queryKey: ['connected-accounts'] });
      toast.success(
        intl.formatMessage({
          id: 'customers.detail.bank.saved',
          defaultMessage: 'Bank account registered.',
        }),
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!pixKey.trim() || !accountHolderName.trim() || save.isPending) return;
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="mt-5 space-y-4 border-t border-navy-100 pt-5">
      <p className="text-sm text-navy-700">
        {intl.formatMessage({
          id: 'customers.detail.bank.add.description',
          defaultMessage:
            'Register a PIX key so this customer can receive invoice settlement. The settlement partner holds the key — we never store it.',
        })}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="pixKey" className="mb-1 block text-xs font-medium text-navy-700">
            {intl.formatMessage({ id: 'customers.new.field.pixKey', defaultMessage: 'PIX key' })}
          </label>
          <Input
            id="pixKey"
            value={pixKey}
            onChange={(e) => setPixKey(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'customers.new.field.pixKey.placeholder',
              defaultMessage: 'CNPJ, CPF, email, phone, or random key',
            })}
            autoComplete="off"
          />
        </div>
        <div>
          <label
            htmlFor="accountHolderName"
            className="mb-1 block text-xs font-medium text-navy-700"
          >
            {intl.formatMessage({
              id: 'customers.new.field.accountHolderName',
              defaultMessage: 'Account holder name',
            })}
          </label>
          <Input
            id="accountHolderName"
            value={accountHolderName}
            onChange={(e) => setAccountHolderName(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!pixKey.trim() || !accountHolderName.trim() || save.isPending}
        >
          {save.isPending
            ? intl.formatMessage({
                id: 'customers.detail.bank.saving',
                defaultMessage: 'Saving...',
              })
            : intl.formatMessage({
                id: 'customers.detail.bank.add.cta',
                defaultMessage: 'Register bank account',
              })}
        </Button>
      </div>
    </form>
  );
}

// Recovery action for a customer stuck awaiting terms (captured, but the
// original ToS-acceptance email never landed or expired) — re-mints the
// single-use link and re-sends it, surfacing the fresh link so the merchant
// can hand it to the customer directly if email delivery fails again.
function ResendTosLink({ customer }: { customer: ConnectedAccountDTO }): JSX.Element {
  const intl = useIntl();
  const qc = useQueryClient();
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);

  const resend = useMutation({
    mutationFn: () => resendConnectedAccountTos(customer.id, { delivery: 'email' }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['connected-account', customer.id] });
      void qc.invalidateQueries({ queryKey: ['connected-accounts'] });
      setAcceptUrl(result.acceptUrl);
      toast.success(
        intl.formatMessage({
          id: 'customers.detail.resend.success',
          defaultMessage: 'Terms link re-sent.',
        }),
      );
      if (!result.emailSent) {
        toast.error(
          intl.formatMessage({
            id: 'customers.detail.resend.emailFailed',
            defaultMessage: "We couldn't email it — copy the link below and send it yourself.",
          }),
        );
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="mt-5 space-y-4 border-t border-navy-100 pt-5">
      <p className="text-sm text-navy-700">
        {intl.formatMessage({
          id: 'customers.detail.resend.description',
          defaultMessage:
            'This customer has not accepted the settlement partner’s terms yet. Re-send the acceptance link if the original email never arrived.',
        })}
      </p>
      <div className="flex justify-end">
        <Button type="button" onClick={() => resend.mutate()} disabled={resend.isPending}>
          {resend.isPending
            ? intl.formatMessage({
                id: 'customers.detail.resend.sending',
                defaultMessage: 'Sending...',
              })
            : intl.formatMessage({
                id: 'customers.detail.resend.cta',
                defaultMessage: 'Resend terms link',
              })}
        </Button>
      </div>
      {acceptUrl ? (
        <CopyableField
          id="resendTosAcceptUrl"
          label={intl.formatMessage({
            id: 'customers.detail.resend.linkLabel',
            defaultMessage: 'Terms acceptance link',
          })}
          value={acceptUrl}
        />
      ) : null}
    </div>
  );
}

// Passive status for a customer that has accepted terms but whose receiver
// isn't created yet. The BlindPay tos.accept webhook drives the deferred
// submission server-side; the detail query polls, so this note resolves on its
// own once the receiver lands — no manual action needed.
function VerificationPending(): JSX.Element {
  const intl = useIntl();
  return (
    <div className="mt-5 space-y-4 border-t border-navy-100 pt-5">
      <p className="text-sm text-navy-700">
        {intl.formatMessage({
          id: 'customers.detail.verificationPending',
          defaultMessage:
            'Terms accepted. Finalizing verification with the settlement partner — this updates automatically once the receiver is created.',
        })}
      </p>
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-navy-600">{label}</dt>
      <dd className="mt-1 text-sm text-navy-900">{children}</dd>
    </div>
  );
}
